export const normalizeLiveSlice = (next, maxSlice) => {
  const safeMax = Math.max(0, Math.trunc(Number(maxSlice) || 0));
  const numeric = Number(next);
  const safeNext = Number.isFinite(numeric) ? Math.trunc(numeric) : 0;
  return Math.max(0, Math.min(safeMax, safeNext));
};

export const readCanonicalUnsignedDomAttribute = (element, attribute) => {
  if (!element || typeof element.getAttribute !== 'function' || typeof attribute !== 'string') return null;
  const raw = element.getAttribute(attribute);
  if (typeof raw !== 'string' || !/^(0|[1-9][0-9]*)$/.test(raw)) return null;
  const numeric = Number(raw);
  return Number.isSafeInteger(numeric) ? numeric : null;
};

export const publishLiveSliceDom = ({ root, next, maxSlice } = {}) => {
  const clamped = normalizeLiveSlice(next, maxSlice);
  if (!root) return clamped;
  root.setAttribute?.('data-viewport-slice-current', String(clamped));
  root.querySelectorAll?.('[data-viewport-live-slice-label="true"]').forEach(label => {
    label.textContent = `${clamped + 1} / ${Math.max(0, Math.trunc(Number(maxSlice) || 0)) + 1}`;
  });
  return clamped;
};

export const clearAuthenticatedRenderedSliceDom = root => {
  if (!root || typeof root.removeAttribute !== 'function') return false;
  root.removeAttribute('data-viewport-rendered-slice');
  root.removeAttribute('data-viewport-rendered-generation');
  return true;
};

export const publishAuthenticatedRenderedSliceDom = ({ root, slice, generation } = {}) => {
  if (!root || typeof root.setAttribute !== 'function' || !Number.isSafeInteger(slice) || slice < 0
      || !Number.isSafeInteger(generation) || generation <= 0) return false;
  root.setAttribute('data-viewport-rendered-slice', String(slice));
  root.setAttribute('data-viewport-rendered-generation', String(generation));
  return true;
};

export const KAIROS_VISIBLE_CINE_FRAME_EVENT = 'kairos:viewport-visible-frame';
export const KAIROS_ACCEPTED_VISIBLE_FRAME_EVENT = 'kairos:viewport-accepted-visible-frame';

const safeEvidenceLineage = candidate => {
  try {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)
        || Object.getPrototypeOf(candidate) !== Object.prototype || !Object.isFrozen(candidate)) return null;
    const keys = Reflect.ownKeys(candidate);
    if (keys.some(key => typeof key !== 'string')) return null;
    const coldKeys = ['evidenceOrdinal', 'requestOrdinal'];
    const warmKeys = ['evidenceOrdinal', 'cacheRootOrdinal', 'consumerOrdinal'];
    const expectedKeys = keys.length === coldKeys.length && coldKeys.every(key => keys.includes(key))
      ? coldKeys
      : keys.length === warmKeys.length && warmKeys.every(key => keys.includes(key))
        ? warmKeys
        : null;
    if (!expectedKeys) return null;
    const lineage = {};
    for (const key of expectedKeys) {
      const descriptor = Object.getOwnPropertyDescriptor(candidate, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')
          || descriptor.enumerable !== true || descriptor.writable !== false
          || descriptor.configurable !== false
          || !Number.isSafeInteger(descriptor.value) || descriptor.value <= 0) return null;
      lineage[key] = descriptor.value;
    }
    return lineage;
  } catch (_) {
    return null;
  }
};

const safeInputCausality = (candidate, expectedSlice) => {
  try {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)
        || Object.getPrototypeOf(candidate) !== Object.prototype || !Object.isFrozen(candidate)) return null;
    const expectedKeys = ['phaseBaseInputOrdinal', 'firstInputOrdinal', 'lastInputOrdinal', 'consumedInputCount',
      'rawDelta', 'effectiveDelta', 'consumedSteps', 'intentSlice', 'consumerFrameOrdinal'];
    const keys = Reflect.ownKeys(candidate);
    if (keys.length !== expectedKeys.length || keys.some(key => typeof key !== 'string')
        || expectedKeys.some(key => !keys.includes(key))) return null;
    const values = {};
    for (const key of expectedKeys) {
      const descriptor = Object.getOwnPropertyDescriptor(candidate, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')
          || descriptor.enumerable !== true || descriptor.writable !== false
          || descriptor.configurable !== false) return null;
      values[key] = descriptor.value;
    }
    if (!Number.isSafeInteger(values.phaseBaseInputOrdinal) || values.phaseBaseInputOrdinal < 0
        || values.phaseBaseInputOrdinal >= values.firstInputOrdinal
        || ![values.firstInputOrdinal, values.lastInputOrdinal, values.consumedInputCount,
      values.consumerFrameOrdinal].every(value => Number.isSafeInteger(value) && value > 0)
        || values.lastInputOrdinal < values.firstInputOrdinal
        || values.consumedInputCount !== values.lastInputOrdinal - values.firstInputOrdinal + 1
        || !Number.isFinite(values.rawDelta) || values.rawDelta === 0
        || !Number.isFinite(values.effectiveDelta) || values.effectiveDelta === 0
        || Math.sign(values.rawDelta) !== Math.sign(values.effectiveDelta)
        || Math.abs(values.effectiveDelta) > Math.abs(values.rawDelta)
        || !Number.isSafeInteger(values.consumedSteps) || values.consumedSteps === 0
        || Math.sign(values.consumedSteps) !== Math.sign(values.effectiveDelta)
        || !Number.isSafeInteger(values.intentSlice) || values.intentSlice !== expectedSlice) return null;
    return values;
  } catch (_) {
    return null;
  }
};

const safeLinkedCausality = (candidate, expectedSlice) => {
  try {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)
        || Object.getPrototypeOf(candidate) !== Object.prototype || !Object.isFrozen(candidate)) return null;
    const expectedKeys = ['commandId', 'sourceViewportIndex', 'sourceSlice', 'targetViewportIndex', 'targetSlice'];
    const keys = Reflect.ownKeys(candidate);
    if (keys.length !== expectedKeys.length || keys.some(key => typeof key !== 'string')
        || expectedKeys.some(key => !keys.includes(key))) return null;
    const values = {};
    for (const key of expectedKeys) {
      const descriptor = Object.getOwnPropertyDescriptor(candidate, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')
          || descriptor.enumerable !== true || descriptor.writable !== false
          || descriptor.configurable !== false) return null;
      values[key] = descriptor.value;
    }
    if (!Number.isSafeInteger(values.commandId) || values.commandId <= 0
        || ![values.sourceViewportIndex, values.sourceSlice, values.targetViewportIndex, values.targetSlice]
          .every(value => Number.isSafeInteger(value) && value >= 0)
        || values.targetSlice !== expectedSlice) return null;
    return values;
  } catch (_) {
    return null;
  }
};

export const publishVisibleFrameDom = ({ root, canvas, slice, generation, eventFactory } = {}) => {
  if (!root || !canvas) return false;
  const safeSlice = Math.max(0, Math.trunc(Number(slice) || 0));
  const safeGeneration = Math.max(0, Math.trunc(Number(generation) || 0));
  canvas.setAttribute?.('data-viewport-preview-slice-current', String(safeSlice));
  canvas.setAttribute?.('data-viewport-preview-generation-current', String(safeGeneration));
  const detail = { slice: safeSlice, generation: safeGeneration, surface: 'preview' };
  const createEvent = eventFactory || ((type, payload) => new CustomEvent(type, { bubbles: true, detail: payload }));
  root.dispatchEvent?.(createEvent(KAIROS_VISIBLE_CINE_FRAME_EVENT, detail));
  return true;
};

export const publishAcceptedVisibleFrameDom = ({
  root,
  surfaceElement,
  slice,
  generation,
  surface,
  sourceUrl,
  requestOrigin,
  observerActive,
  revealed,
  evidenceLineage,
  inputCausality,
  linkedCausality,
  eventFactory,
} = {}) => {
  if (observerActive !== true || revealed !== true || !root || !surfaceElement) return false;
  if (!['preview', 'active-image'].includes(surface)) return false;
  const numericSlice = Number(slice);
  const numericGeneration = Number(generation);
  if (!Number.isFinite(numericSlice) || numericSlice < 0
      || !Number.isFinite(numericGeneration) || numericGeneration < 0
      || typeof sourceUrl !== 'string' || sourceUrl.length === 0
      || typeof requestOrigin !== 'string' || requestOrigin.length === 0) return false;
  const safeSlice = Math.trunc(numericSlice);
  const safeGeneration = Math.trunc(numericGeneration);
  surfaceElement.setAttribute?.('data-viewport-accepted-slice-current', String(safeSlice));
  surfaceElement.setAttribute?.('data-viewport-accepted-generation-current', String(safeGeneration));
  surfaceElement.setAttribute?.('data-viewport-accepted-surface-current', surface);
  const lineage = safeEvidenceLineage(evidenceLineage);
  const causality = safeInputCausality(inputCausality, safeSlice);
  const linked = safeLinkedCausality(linkedCausality, safeSlice);
  const detail = {
    slice: safeSlice,
    generation: safeGeneration,
    surface,
    sourceUrl,
    requestOrigin,
    ...(lineage || {}),
    ...(causality || {}),
    ...(linked || {}),
  };
  const createEvent = eventFactory || ((type, payload) => new CustomEvent(type, { bubbles: true, detail: payload }));
  root.dispatchEvent?.(createEvent(KAIROS_ACCEPTED_VISIBLE_FRAME_EVENT, detail));
  return true;
};

export const transferAcceptedVisibleFrameDom = ({
  surfaceElement,
  slice,
  generation,
  surface,
} = {}) => {
  if (typeof surfaceElement?.setAttribute !== 'function' || !['preview', 'active-image'].includes(surface)
      || !Number.isSafeInteger(slice) || slice < 0
      || !Number.isSafeInteger(generation) || generation <= 0) return false;
  surfaceElement.setAttribute('data-viewport-accepted-slice-current', String(slice));
  surfaceElement.setAttribute('data-viewport-accepted-generation-current', String(generation));
  surfaceElement.setAttribute('data-viewport-accepted-surface-current', surface);
  return true;
};
